import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { FileDown, Plus } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { ChecklistForm } from "@/components/ChecklistForm";
import { useAutoRefresh } from "@/lib/useAutoRefresh";

export const KIND_LABELS = { vano: "Vano sanitario", zaino: "Zaino", mezzo: "Mezzo" };

export async function downloadChecklistPdf(c) {
  try {
    const res = await api.get(`/checklists/${c.id}/pdf`, { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const link = document.createElement("a");
    link.href = url;
    link.download = `checklist-${c.kind || "vano"}-${c.service_name}.pdf`;
    link.click();
    URL.revokeObjectURL(url);
  } catch {
    toast.error("Impossibile scaricare il PDF");
  }
}

export function ChecklistDialog({ svc, currentUser, open, onOpenChange }) {
  const [list, setList] = useState([]);
  const [creating, setCreating] = useState(null);
  const canFill = svc.status === "coperto" && svc.is_signed_up;

  const load = () => api.get(`/services/${svc.id}/checklists`).then((r) => setList(r.data)).catch(() => setList([]));
  useEffect(() => { if (open) { load(); setCreating(null); } }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  useAutoRefresh(load, 20000, open && !creating);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="checklist-dialog">
        <DialogHeader>
          <DialogTitle>{creating ? `Check-list ${KIND_LABELS[creating]}` : "Check-list del servizio"}</DialogTitle>
          <DialogDescription>{svc.event_name}</DialogDescription>
        </DialogHeader>
        {creating ? (
          <ChecklistForm key={creating} kind={creating} svc={svc} currentUser={currentUser} onCancel={() => setCreating(null)} onSaved={() => { setCreating(null); load(); }} />
        ) : (
          <div className="space-y-3">
            {canFill && (
              <div className="flex flex-wrap gap-2">
                <Button onClick={() => setCreating("vano")} data-testid="checklist-new" className="rounded-full">
                  <Plus className="h-4 w-4 mr-1" /> Check-list Vano sanitario
                </Button>
                <Button onClick={() => setCreating("zaino")} data-testid="checklist-new-zaino" className="rounded-full" variant="secondary">
                  <Plus className="h-4 w-4 mr-1" /> Check-list Zaino
                </Button>
                {(currentUser.qualifications || []).includes("Autista") && (
                  <Button onClick={() => setCreating("mezzo")} data-testid="checklist-new-mezzo" className="rounded-full" variant="outline">
                    <Plus className="h-4 w-4 mr-1" /> Check-list Mezzo
                  </Button>
                )}
              </div>
            )}
            {list.length === 0 ? (
              <p className="text-sm text-muted-foreground" data-testid="checklist-empty">Nessuna check-list compilata per questo servizio.</p>
            ) : list.map((c) => (
              <div key={c.id} data-testid={`checklist-item-${c.id}`} className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-border px-3 py-2">
                <div className="min-w-0">
                  <p className="text-sm font-semibold"><Badge variant="secondary" className="mr-2">{KIND_LABELS[c.kind || "vano"]}</Badge>{c.compiled_by_name}{c.extra_operators ? `, ${c.extra_operators}` : ""}</p>
                  <p className="text-xs text-muted-foreground">Firmata il {new Date(c.signed_at).toLocaleString("it-IT")}{c.vehicle_name ? ` · ${c.vehicle_name}` : ""}</p>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className={c.missing_count ? "text-red-700 border-red-200" : "text-emerald-700 border-emerald-200"}>
                    {c.missing_count ? `${c.missing_count} mancanti` : "Completa"}
                  </Badge>
                  <Button size="sm" variant="outline" onClick={() => downloadChecklistPdf(c)} data-testid={`checklist-pdf-${c.id}`} className="rounded-full">
                    <FileDown className="h-4 w-4 mr-1" /> PDF
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
